import { api } from '@lib/api';
import type { IntegranteDto, MetodoOtp, MetodoOtpInfo } from './portal.service';

/**
 * Cliente del wizard de activación de un `legajo_bis` (Fase 2 alta-directa).
 * Se entra por el `activacionToken` del bis; el OTP va al TITULAR del legajo
 * destino ya afiliado. La DDJJ reusa `getDdjj/guardarDdjj({ bis: true })` de
 * `portal.service`; los adjuntos, los endpoints compartidos de archivos-ddjj.
 */

export interface BisNeedsOtp {
  needsOtp: true;
  documento: string;
  titularNombre: string;
  primerNombre: string;
  metodos: MetodoOtpInfo[];
}

/**
 * Detalle de cuota MS2 para el/los integrante(s) nuevo(s). Solo `aplica` cuando
 * el grupo destino ya tiene MS2: en ese caso, por cada integrante, lo que SUMA a
 * la cuota mensual del titular (delta MS2 − base, **sin IVA**). Alimenta el popup
 * de confirmación antes de finalizar.
 */
export type CuotaMs2Bis =
  | { aplica: false }
  | {
      aplica: true;
      vigenciaPrecios: string;
      integrantes: Array<{ nombre: string; cuotaMensualSinIva: number }>;
    };

export interface BisData {
  needsOtp: false;
  bis: { id: string; numero: string; estadoActivacion: 'en_validacion' | 'validado' };
  titular: { nombre: string; numeroLegajoDestino: string; destinoDadoDeAlta: boolean };
  integrantes: IntegranteDto[];
  /** Opcional: backend viejo puede no mandarlo. */
  cuotaMs2?: CuotaMs2Bis;
}

export interface BisNoDisponible {
  noDisponible: true;
  motivo: string;
  mensaje: string;
}

export type BisOnboarding = BisNeedsOtp | BisData | BisNoDisponible;

export function isBisNoDisponible(r: BisOnboarding): r is BisNoDisponible {
  return 'noDisponible' in r && r.noDisponible === true;
}
export function isBisNeedsOtp(r: BisOnboarding): r is BisNeedsOtp {
  return 'needsOtp' in r && r.needsOtp === true;
}

export async function getBisByToken(token: string): Promise<BisOnboarding> {
  const { data } = await api.get<BisOnboarding>(`/portal-bis/${token}`);
  return data;
}

export async function solicitarOtpBis(
  token: string,
  metodo: MetodoOtp,
): Promise<{ ok: boolean; expiraAt: string; canal: string }> {
  const { data } = await api.post(`/portal-bis/${token}/otp/solicitar`, { metodo });
  return data;
}

export async function validarOtpBis(token: string, code: string): Promise<{ ok: boolean }> {
  const { data } = await api.post(`/portal-bis/${token}/otp/validar`, { code });
  return data;
}

export async function patchIntegranteBis(
  token: string,
  asociadoId: string,
  patch: Partial<IntegranteDto>,
): Promise<IntegranteDto> {
  const { data } = await api.put<IntegranteDto>(
    `/portal-bis/${token}/integrante/${asociadoId}`,
    patch,
  );
  return data;
}

export async function finalizarBis(
  token: string,
): Promise<{ ok: boolean; numero: string; estadoActivacion: 'validado' }> {
  const { data } = await api.post(`/portal-bis/${token}/finalizar`, {});
  return data;
}
